import { DateTime, NaiveDate, SemanticTime as _SemanticTime, Utc } from "@tento-chrono";
import { Rational as _Rational } from "@tento-core/rational";
import { Uuid } from "@tento-core/uuid";
import { RowId } from "@tento-lona/sheets";
import { BlogListResponse as _BlogListResponse, BlogPostDetail as _BlogPostDetail, BlogPostSummary as _BlogPostSummary } from "@tento-ui/brand/blog/@generated/models";
import { Field, Model, Scalar } from "@tento-core/model";

/**
 * The server's wire types and companion codecs, generated from its OpenAPI
 * document. Object companions validate, hydrate and serialize wire values.
 */
export namespace Network {
  export interface ApiKeyItem {
    createdAt: DateTime<Utc>;
    expiresAt: DateTime<Utc>;
    tokenPrefix: string;
    ttlSeconds: number;
    userId: string;
  }
  export interface ApiKeysListResponse {
    items: ApiKeyItem[];
    total: number;
  }
  export interface AvailableSlot {
    endTime: DateTime<Utc>;
    startTime: DateTime<Utc>;
  }
  export interface AvailabilityResponse {
    availableSlots: AvailableSlot[];
    date: NaiveDate;
  }
  export type BookingStatus = "pending" | "confirmed" | "cancelled" | "completed";
  export interface Booking {
    bookerEmail: string;
    bookerName: string;
    createdAt: DateTime<Utc>;
    endTime: DateTime<Utc>;
    id: Uuid;
    schedulingLinkId: Uuid;
    startTime: DateTime<Utc>;
    status: BookingStatus;
  }
  export interface CalendarOpenRequest {
    foreground: boolean;
  }
  export interface Changes {
    added?: Option<string[]>;
    changed?: Option<string[]>;
    fixed?: Option<string[]>;
  }
  export interface ChangeLogEntry {
    changes: Changes;
    date: NaiveDate;
    images?: Option<string[]>;
    version: string;
  }
  export interface ModelInfo {
    id: string;
    label: string;
  }
  export interface ProviderInfo {
    defaultModel: string;
    label: string;
    models: ModelInfo[];
    provider: string;
  }
  export interface ChatModelsResponse {
    defaultProvider: string;
    providers: ProviderInfo[];
  }
  export interface ChatRequest {
    message: string;
    model?: Option<string>;
    provider?: Option<string>;
    sessionId?: Option<string>;
    timezone?: Option<string>;
  }
  export interface ClaimResponse {
    status: string;
    tier?: Option<string>;
  }
  export interface CrdtSyncPayload {
    bytesBase64: string;
    docId: string;
    kind: string;
    replicaId: string;
    version?: Option<UntypedJson>;
  }
  export interface CrdtEnvelope {
    attributes?: Option<UntypedJson>;
    from: string;
    id: string;
    payload?: Option<CrdtSyncPayload>;
    to: string;
    tsMs: number;
    type: string;
  }
  export interface CrdtBootstrapResponse {
    nextCursor: number;
    snapshotEnvelope?: Option<CrdtEnvelope>;
  }
  export interface CrdtCreateRoomRequest {
    name?: Option<string>;
    namespace?: Option<string>;
    ttlMinutes?: Option<number>;
  }
  export interface CrdtCreateRoomResponse {
    createdAt: DateTime<Utc>;
    expiresAt?: Option<DateTime<Utc>>;
    isPublic: boolean;
    namespace: string;
    roomId: string;
    roomToken: string;
  }
  export interface CrdtEnvelopeRecord {
    envelope: CrdtEnvelope;
    receivedAt: DateTime<Utc>;
    seq: number;
  }
  export interface CrdtRoomSummary {
    createdAt: DateTime<Utc>;
    eventCount: number;
    expiresAt?: Option<DateTime<Utc>>;
    isPublic: boolean;
    name: string;
    namespace: string;
    roomId: string;
  }
  export interface CrdtListRoomsResponse {
    rooms: CrdtRoomSummary[];
  }
  export interface CrdtPostEventRequest {
    envelope: CrdtEnvelope;
    roomToken?: Option<string>;
  }
  export interface CrdtPullEventsResponse {
    events: CrdtEnvelopeRecord[];
    nextCursor: number;
  }
  export interface CreateBookingRequest {
    bookerEmail: string;
    bookerName: string;
    startTime: DateTime<Utc>;
  }
  export interface CreatedApiKeyResponse {
    expiresAt: DateTime<Utc>;
    token: string;
  }
  export interface DateRange {
    end: NaiveDate;
    start: NaiveDate;
  }
  export interface SyncStateInfo {
    futureLastSync?: Option<DateTime<Utc>>;
    futureSyncedTo?: Option<NaiveDate>;
    historicalSyncedTo?: Option<NaiveDate>;
    lastSyncTime: DateTime<Utc>;
    needsHistoricalResync?: Option<boolean>;
    timezoneId: string;
  }
  export interface WeatherRowInfo {
    label?: Option<string>;
    lid: number;
    lookupKey: string;
    syncState?: Option<SyncStateInfo>;
  }
  export interface DebugWeatherResponse {
    rows: WeatherRowInfo[];
  }
  export type Interface = "email_capture" | "oauth";
  export type Reason = "initial" | "sign_in_anyway" | "viewport_change";
  export interface Exposure {
    interface: Interface;
    page_id: string;
    reason: Reason;
    sequence: number;
  }
  export type NodeKind = "section" | "stack" | "grid" | "heading" | "prose" | "media" | "cta" | "list" | "hero" | "pill" | "feature-grid" | "card" | "social-proof" | "comparison" | "faq" | "pricing" | "integrations" | "post-list" | "post-body" | "search-filter" | "nav" | "footer" | "breadcrumb";
  export interface ObservationReceipt {
    admittedAtMs: number;
    eventId: string;
    journalEpoch: string;
    journalSequence: number;
    observationId: string;
    policy: string;
    schema: string;
  }
  export interface ObservationRequest {
    dimensions?: Option<UntypedJson>;
    kind: string;
    observation_id: string;
  }
  export interface OnboardingStepRequest {
    outcome: string;
    step: string;
  }
  export interface PageMeta {
    canonicalUrl?: Option<string>;
    description: string;
    longTailKeywords?: Option<string[]>;
    ogDescription?: Option<string>;
    ogImage?: Option<string>;
    ogTitle?: Option<string>;
    primaryKeyword: string;
    schemaTypes?: Option<string[]>;
    secondaryKeywords: string[];
    title: string;
  }
  export interface PageNode {
    children?: Option<PageNode[]>;
    id: string;
    kind: NodeKind;
    props: {
    };
  }
  export type ThemeRef = string | {
    tokens: string;
    variant: string;
  };
  export interface PageModel {
    decorators?: Option<string[]>;
    meta: PageMeta;
    nav: string;
    nodes: PageNode[];
    route: string;
    theme: ThemeRef;
  }
  export interface PromoCampaignCode {
    claimed: boolean;
    code: string;
  }
  export interface PromoCampaignRequest {
    campaign: string;
  }
  export interface PromoCampaignResponse {
    codes: PromoCampaignCode[];
  }
  export interface SheetCellSchema {
    data?: Option<UntypedJson>;
    date: SemanticTime;
    endDate?: Option<SemanticTime>;
    id?: Option<string>;
  }
  export type SheetCell = SheetCellSchema;
  export interface SheetRowDataResponse {
    data: SheetCell[];
    dataRange?: Option<DateRange>;
    id: RowId;
    label?: Option<string>;
    lookupKey?: Option<string>;
    metadata?: Option<UntypedJson>;
    rowType?: Option<string>;
    syncKeys?: Option<string[]>;
  }
  export type SyncStatus = "pending" | "running" | "complete" | "failed";
  export interface SyncStatusResponse {
    error?: Option<string>;
    pollUrl?: Option<string>;
    status: SyncStatus;
  }
  export interface TracingEntriesRequest {
    entries: UntypedJson[];
  }
  export interface WarmRequest {
    range?: Option<DateRange>;
    rowKeys: string[];
  }
  export interface WarmResponse {
    syncKeys: string[];
  }
  export type SemanticTime = _SemanticTime;
  export namespace ApiKeyItem {
    /** The field table that validates, hydrates and serializes a `ApiKeyItem`. */
    export const MODEL: Model.Object<ApiKeyItem> = Model.object<ApiKeyItem>({
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      expiresAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      tokenPrefix: Field.required(Scalar.string),
      ttlSeconds: Field.required(Scalar.number),
      userId: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ApiKeyItem`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ApiKeyItem = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ApiKeyItem`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ApiKeyItem> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ApiKeyItem) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ApiKeysListResponse {
    /** The field table that validates, hydrates and serializes a `ApiKeysListResponse`. */
    export const MODEL: Model.Object<ApiKeysListResponse> = Model.object<ApiKeysListResponse>({
      items: Field.required(Model.array(Model.defer(() => ApiKeyItem.MODEL))),
      total: Field.required(Scalar.number),
    });
    /** The wire JSON `raw` as a `ApiKeysListResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ApiKeysListResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ApiKeysListResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ApiKeysListResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ApiKeysListResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace AvailableSlot {
    /** The field table that validates, hydrates and serializes a `AvailableSlot`. */
    export const MODEL: Model.Object<AvailableSlot> = Model.object<AvailableSlot>({
      endTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      startTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
    /** The wire JSON `raw` as a `AvailableSlot`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => AvailableSlot = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `AvailableSlot`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<AvailableSlot> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: AvailableSlot) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace AvailabilityResponse {
    /** The field table that validates, hydrates and serializes a `AvailabilityResponse`. */
    export const MODEL: Model.Object<AvailabilityResponse> = Model.object<AvailabilityResponse>({
      availableSlots: Field.required(Model.array(Model.defer(() => AvailableSlot.MODEL))),
      date: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
    });
    /** The wire JSON `raw` as a `AvailabilityResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => AvailabilityResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `AvailabilityResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<AvailabilityResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: AvailabilityResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace Booking {
    /** The field table that validates, hydrates and serializes a `Booking`. */
    export const MODEL: Model.Object<Booking> = Model.object<Booking>({
      bookerEmail: Field.required(Scalar.string),
      bookerName: Field.required(Scalar.string),
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      endTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      id: Field.required(Scalar.checked<string, Uuid>("Uuid", Scalar.string, Uuid.parse, null)),
      schedulingLinkId: Field.required(Scalar.checked<string, Uuid>("Uuid", Scalar.string, Uuid.parse, null)),
      startTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      status: Field.required(Scalar.stringUnion(["pending", "confirmed", "cancelled", "completed"] as const)),
    });
    /** The wire JSON `raw` as a `Booking`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => Booking = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Booking`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<Booking> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Booking) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CalendarOpenRequest {
    /** The field table that validates, hydrates and serializes a `CalendarOpenRequest`. */
    export const MODEL: Model.Object<CalendarOpenRequest> = Model.object<CalendarOpenRequest>({
      foreground: Field.required(Scalar.boolean),
    });
    /** The wire JSON `raw` as a `CalendarOpenRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CalendarOpenRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CalendarOpenRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CalendarOpenRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CalendarOpenRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace Changes {
    /** The field table that validates, hydrates and serializes a `Changes`. */
    export const MODEL: Model.Object<Changes> = Model.object<Changes>({
      added: Field.optional(Model.array(Scalar.string)),
      changed: Field.optional(Model.array(Scalar.string)),
      fixed: Field.optional(Model.array(Scalar.string)),
    });
    /** The wire JSON `raw` as a `Changes`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => Changes = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Changes`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<Changes> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Changes) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ChangeLogEntry {
    /** The field table that validates, hydrates and serializes a `ChangeLogEntry`. */
    export const MODEL: Model.Object<ChangeLogEntry> = Model.object<ChangeLogEntry>({
      changes: Field.required(Model.defer(() => Changes.MODEL)),
      date: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      images: Field.optional(Model.array(Scalar.string)),
      version: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ChangeLogEntry`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ChangeLogEntry = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ChangeLogEntry`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ChangeLogEntry> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ChangeLogEntry) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ModelInfo {
    /** The field table that validates, hydrates and serializes a `ModelInfo`. */
    export const MODEL: Model.Object<ModelInfo> = Model.object<ModelInfo>({
      id: Field.required(Scalar.string),
      label: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ModelInfo`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ModelInfo = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ModelInfo`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ModelInfo> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ModelInfo) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ProviderInfo {
    /** The field table that validates, hydrates and serializes a `ProviderInfo`. */
    export const MODEL: Model.Object<ProviderInfo> = Model.object<ProviderInfo>({
      defaultModel: Field.required(Scalar.string),
      label: Field.required(Scalar.string),
      models: Field.required(Model.array(Model.defer(() => ModelInfo.MODEL))),
      provider: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ProviderInfo`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ProviderInfo = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ProviderInfo`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ProviderInfo> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ProviderInfo) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ChatModelsResponse {
    /** The field table that validates, hydrates and serializes a `ChatModelsResponse`. */
    export const MODEL: Model.Object<ChatModelsResponse> = Model.object<ChatModelsResponse>({
      defaultProvider: Field.required(Scalar.string),
      providers: Field.required(Model.array(Model.defer(() => ProviderInfo.MODEL))),
    });
    /** The wire JSON `raw` as a `ChatModelsResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ChatModelsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ChatModelsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ChatModelsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ChatModelsResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ChatRequest {
    /** The field table that validates, hydrates and serializes a `ChatRequest`. */
    export const MODEL: Model.Object<ChatRequest> = Model.object<ChatRequest>({
      message: Field.required(Scalar.string),
      model: Field.optional(Scalar.string),
      provider: Field.optional(Scalar.string),
      sessionId: Field.optional(Scalar.string),
      timezone: Field.optional(Scalar.string),
    });
    /** The wire JSON `raw` as a `ChatRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ChatRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ChatRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ChatRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ChatRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ClaimResponse {
    /** The field table that validates, hydrates and serializes a `ClaimResponse`. */
    export const MODEL: Model.Object<ClaimResponse> = Model.object<ClaimResponse>({
      status: Field.required(Scalar.string),
      tier: Field.optional(Scalar.string),
    });
    /** The wire JSON `raw` as a `ClaimResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ClaimResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ClaimResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ClaimResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ClaimResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtSyncPayload {
    /** The field table that validates, hydrates and serializes a `CrdtSyncPayload`. */
    export const MODEL: Model.Object<CrdtSyncPayload> = Model.object<CrdtSyncPayload>({
      bytesBase64: Field.required(Scalar.string),
      docId: Field.required(Scalar.string),
      kind: Field.required(Scalar.string),
      replicaId: Field.required(Scalar.string),
      version: Field.optional(Scalar.json),
    });
    /** The wire JSON `raw` as a `CrdtSyncPayload`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtSyncPayload = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtSyncPayload`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtSyncPayload> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtSyncPayload) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtEnvelope {
    /** The field table that validates, hydrates and serializes a `CrdtEnvelope`. */
    export const MODEL: Model.Object<CrdtEnvelope> = Model.object<CrdtEnvelope>({
      attributes: Field.optional(Scalar.json),
      from: Field.required(Scalar.string),
      id: Field.required(Scalar.string),
      payload: Field.optional(Model.defer(() => CrdtSyncPayload.MODEL)),
      to: Field.required(Scalar.string),
      tsMs: Field.required(Scalar.number),
      type: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `CrdtEnvelope`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtEnvelope = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtEnvelope`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtEnvelope> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtEnvelope) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtBootstrapResponse {
    /** The field table that validates, hydrates and serializes a `CrdtBootstrapResponse`. */
    export const MODEL: Model.Object<CrdtBootstrapResponse> = Model.object<CrdtBootstrapResponse>({
      nextCursor: Field.required(Scalar.number),
      snapshotEnvelope: Field.optional(Model.defer(() => CrdtEnvelope.MODEL)),
    });
    /** The wire JSON `raw` as a `CrdtBootstrapResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtBootstrapResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtBootstrapResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtBootstrapResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtBootstrapResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtCreateRoomRequest {
    /** The field table that validates, hydrates and serializes a `CrdtCreateRoomRequest`. */
    export const MODEL: Model.Object<CrdtCreateRoomRequest> = Model.object<CrdtCreateRoomRequest>({
      name: Field.optional(Scalar.string),
      namespace: Field.optional(Scalar.string),
      ttlMinutes: Field.optional(Scalar.number),
    });
    /** The wire JSON `raw` as a `CrdtCreateRoomRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtCreateRoomRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtCreateRoomRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtCreateRoomRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtCreateRoomRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtCreateRoomResponse {
    /** The field table that validates, hydrates and serializes a `CrdtCreateRoomResponse`. */
    export const MODEL: Model.Object<CrdtCreateRoomResponse> = Model.object<CrdtCreateRoomResponse>({
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      expiresAt: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      isPublic: Field.required(Scalar.boolean),
      namespace: Field.required(Scalar.string),
      roomId: Field.required(Scalar.string),
      roomToken: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `CrdtCreateRoomResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtCreateRoomResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtCreateRoomResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtCreateRoomResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtCreateRoomResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtEnvelopeRecord {
    /** The field table that validates, hydrates and serializes a `CrdtEnvelopeRecord`. */
    export const MODEL: Model.Object<CrdtEnvelopeRecord> = Model.object<CrdtEnvelopeRecord>({
      envelope: Field.required(Model.defer(() => CrdtEnvelope.MODEL)),
      receivedAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      seq: Field.required(Scalar.number),
    });
    /** The wire JSON `raw` as a `CrdtEnvelopeRecord`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtEnvelopeRecord = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtEnvelopeRecord`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtEnvelopeRecord> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtEnvelopeRecord) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtRoomSummary {
    /** The field table that validates, hydrates and serializes a `CrdtRoomSummary`. */
    export const MODEL: Model.Object<CrdtRoomSummary> = Model.object<CrdtRoomSummary>({
      createdAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      eventCount: Field.required(Scalar.number),
      expiresAt: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      isPublic: Field.required(Scalar.boolean),
      name: Field.required(Scalar.string),
      namespace: Field.required(Scalar.string),
      roomId: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `CrdtRoomSummary`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtRoomSummary = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtRoomSummary`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtRoomSummary> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtRoomSummary) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtListRoomsResponse {
    /** The field table that validates, hydrates and serializes a `CrdtListRoomsResponse`. */
    export const MODEL: Model.Object<CrdtListRoomsResponse> = Model.object<CrdtListRoomsResponse>({
      rooms: Field.required(Model.array(Model.defer(() => CrdtRoomSummary.MODEL))),
    });
    /** The wire JSON `raw` as a `CrdtListRoomsResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtListRoomsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtListRoomsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtListRoomsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtListRoomsResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtPostEventRequest {
    /** The field table that validates, hydrates and serializes a `CrdtPostEventRequest`. */
    export const MODEL: Model.Object<CrdtPostEventRequest> = Model.object<CrdtPostEventRequest>({
      envelope: Field.required(Model.defer(() => CrdtEnvelope.MODEL)),
      roomToken: Field.optional(Scalar.string),
    });
    /** The wire JSON `raw` as a `CrdtPostEventRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtPostEventRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtPostEventRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtPostEventRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtPostEventRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CrdtPullEventsResponse {
    /** The field table that validates, hydrates and serializes a `CrdtPullEventsResponse`. */
    export const MODEL: Model.Object<CrdtPullEventsResponse> = Model.object<CrdtPullEventsResponse>({
      events: Field.required(Model.array(Model.defer(() => CrdtEnvelopeRecord.MODEL))),
      nextCursor: Field.required(Scalar.number),
    });
    /** The wire JSON `raw` as a `CrdtPullEventsResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CrdtPullEventsResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CrdtPullEventsResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CrdtPullEventsResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CrdtPullEventsResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CreateBookingRequest {
    /** The field table that validates, hydrates and serializes a `CreateBookingRequest`. */
    export const MODEL: Model.Object<CreateBookingRequest> = Model.object<CreateBookingRequest>({
      bookerEmail: Field.required(Scalar.string),
      bookerName: Field.required(Scalar.string),
      startTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
    });
    /** The wire JSON `raw` as a `CreateBookingRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CreateBookingRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CreateBookingRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CreateBookingRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CreateBookingRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace CreatedApiKeyResponse {
    /** The field table that validates, hydrates and serializes a `CreatedApiKeyResponse`. */
    export const MODEL: Model.Object<CreatedApiKeyResponse> = Model.object<CreatedApiKeyResponse>({
      expiresAt: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      token: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `CreatedApiKeyResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => CreatedApiKeyResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `CreatedApiKeyResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<CreatedApiKeyResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: CreatedApiKeyResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace DateRange {
    /** The field table that validates, hydrates and serializes a `DateRange`. */
    export const MODEL: Model.Object<DateRange> = Model.object<DateRange>({
      end: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      start: Field.required(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
    });
    /** The wire JSON `raw` as a `DateRange`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => DateRange = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `DateRange`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<DateRange> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: DateRange) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace SyncStateInfo {
    /** The field table that validates, hydrates and serializes a `SyncStateInfo`. */
    export const MODEL: Model.Object<SyncStateInfo> = Model.object<SyncStateInfo>({
      futureLastSync: Field.optional(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      futureSyncedTo: Field.optional(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      historicalSyncedTo: Field.optional(Scalar.checked<string, NaiveDate>("NaiveDate", Scalar.string, NaiveDate.parseOpt, null)),
      lastSyncTime: Field.required(Scalar.checked<string, DateTime<Utc>>("DateTime<Utc>", Scalar.string, Utc.parse, null)),
      needsHistoricalResync: Field.optional(Scalar.boolean),
      timezoneId: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `SyncStateInfo`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => SyncStateInfo = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SyncStateInfo`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<SyncStateInfo> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SyncStateInfo) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace WeatherRowInfo {
    /** The field table that validates, hydrates and serializes a `WeatherRowInfo`. */
    export const MODEL: Model.Object<WeatherRowInfo> = Model.object<WeatherRowInfo>({
      label: Field.optional(Scalar.string),
      lid: Field.required(Scalar.number),
      lookupKey: Field.required(Scalar.string),
      syncState: Field.optional(Model.defer(() => SyncStateInfo.MODEL)),
    });
    /** The wire JSON `raw` as a `WeatherRowInfo`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => WeatherRowInfo = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `WeatherRowInfo`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<WeatherRowInfo> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: WeatherRowInfo) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace DebugWeatherResponse {
    /** The field table that validates, hydrates and serializes a `DebugWeatherResponse`. */
    export const MODEL: Model.Object<DebugWeatherResponse> = Model.object<DebugWeatherResponse>({
      rows: Field.required(Model.array(Model.defer(() => WeatherRowInfo.MODEL))),
    });
    /** The wire JSON `raw` as a `DebugWeatherResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => DebugWeatherResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `DebugWeatherResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<DebugWeatherResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: DebugWeatherResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace Exposure {
    /** The field table that validates, hydrates and serializes a `Exposure`. */
    export const MODEL: Model.Object<Exposure> = Model.object<Exposure>({
      interface: Field.required(Scalar.stringUnion(["email_capture", "oauth"] as const)),
      page_id: Field.required(Scalar.string),
      reason: Field.required(Scalar.stringUnion(["initial", "sign_in_anyway", "viewport_change"] as const)),
      sequence: Field.required(Scalar.number),
    });
    /** The wire JSON `raw` as a `Exposure`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => Exposure = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `Exposure`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<Exposure> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: Exposure) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ObservationReceipt {
    /** The field table that validates, hydrates and serializes a `ObservationReceipt`. */
    export const MODEL: Model.Object<ObservationReceipt> = Model.object<ObservationReceipt>({
      admittedAtMs: Field.required(Scalar.number),
      eventId: Field.required(Scalar.string),
      journalEpoch: Field.required(Scalar.string),
      journalSequence: Field.required(Scalar.number),
      observationId: Field.required(Scalar.string),
      policy: Field.required(Scalar.string),
      schema: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ObservationReceipt`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ObservationReceipt = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ObservationReceipt`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ObservationReceipt> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ObservationReceipt) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ObservationRequest {
    /** The field table that validates, hydrates and serializes a `ObservationRequest`. */
    export const MODEL: Model.Object<ObservationRequest> = Model.object<ObservationRequest>({
      dimensions: Field.optional(Scalar.json),
      kind: Field.required(Scalar.string),
      observation_id: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `ObservationRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => ObservationRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `ObservationRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<ObservationRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: ObservationRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace OnboardingStepRequest {
    /** The field table that validates, hydrates and serializes a `OnboardingStepRequest`. */
    export const MODEL: Model.Object<OnboardingStepRequest> = Model.object<OnboardingStepRequest>({
      outcome: Field.required(Scalar.string),
      step: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `OnboardingStepRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => OnboardingStepRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `OnboardingStepRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<OnboardingStepRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: OnboardingStepRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PageMeta {
    /** The field table that validates, hydrates and serializes a `PageMeta`. */
    export const MODEL: Model.Object<PageMeta> = Model.object<PageMeta>({
      canonicalUrl: Field.optional(Scalar.string),
      description: Field.required(Scalar.string),
      longTailKeywords: Field.optional(Model.array(Scalar.string)),
      ogDescription: Field.optional(Scalar.string),
      ogImage: Field.optional(Scalar.string),
      ogTitle: Field.optional(Scalar.string),
      primaryKeyword: Field.required(Scalar.string),
      schemaTypes: Field.optional(Model.array(Scalar.string)),
      secondaryKeywords: Field.required(Model.array(Scalar.string)),
      title: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `PageMeta`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PageMeta = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PageMeta`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PageMeta> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PageMeta) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PageNode {
    /** The field table that validates, hydrates and serializes a `PageNode`. */
    export const MODEL: Model.Object<PageNode> = Model.object<PageNode>({
      children: Field.optional(Model.array(Model.defer(() => PageNode.MODEL))),
      id: Field.required(Scalar.string),
      kind: Field.required(Scalar.stringUnion(["section", "stack", "grid", "heading", "prose", "media", "cta", "list", "hero", "pill", "feature-grid", "card", "social-proof", "comparison", "faq", "pricing", "integrations", "post-list", "post-body", "search-filter", "nav", "footer", "breadcrumb"] as const)),
      props: Field.required(Scalar.json),
    });
    /** The wire JSON `raw` as a `PageNode`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PageNode = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PageNode`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PageNode> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PageNode) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PageModel {
    /** The field table that validates, hydrates and serializes a `PageModel`. */
    export const MODEL: Model.Object<PageModel> = Model.object<PageModel>({
      decorators: Field.optional(Model.array(Scalar.string)),
      meta: Field.required(Model.defer(() => PageMeta.MODEL)),
      nav: Field.required(Scalar.string),
      nodes: Field.required(Model.array(Model.defer(() => PageNode.MODEL))),
      route: Field.required(Scalar.string),
      theme: Field.required(Model.union([Scalar.string, Model.object({ tokens: Field.required(Scalar.string), variant: Field.required(Scalar.string) })])),
    });
    /** The wire JSON `raw` as a `PageModel`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PageModel = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PageModel`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PageModel> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PageModel) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PromoCampaignCode {
    /** The field table that validates, hydrates and serializes a `PromoCampaignCode`. */
    export const MODEL: Model.Object<PromoCampaignCode> = Model.object<PromoCampaignCode>({
      claimed: Field.required(Scalar.boolean),
      code: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `PromoCampaignCode`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PromoCampaignCode = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PromoCampaignCode`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PromoCampaignCode> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PromoCampaignCode) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PromoCampaignRequest {
    /** The field table that validates, hydrates and serializes a `PromoCampaignRequest`. */
    export const MODEL: Model.Object<PromoCampaignRequest> = Model.object<PromoCampaignRequest>({
      campaign: Field.required(Scalar.string),
    });
    /** The wire JSON `raw` as a `PromoCampaignRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PromoCampaignRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PromoCampaignRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PromoCampaignRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PromoCampaignRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace PromoCampaignResponse {
    /** The field table that validates, hydrates and serializes a `PromoCampaignResponse`. */
    export const MODEL: Model.Object<PromoCampaignResponse> = Model.object<PromoCampaignResponse>({
      codes: Field.required(Model.array(Model.defer(() => PromoCampaignCode.MODEL))),
    });
    /** The wire JSON `raw` as a `PromoCampaignResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => PromoCampaignResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `PromoCampaignResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<PromoCampaignResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: PromoCampaignResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetCellSchema {
    /** The field table that validates, hydrates and serializes a `SheetCellSchema`. */
    export const MODEL: Model.Object<SheetCellSchema> = Model.object<SheetCellSchema>({
      data: Field.optional(Scalar.json),
      date: Field.required(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      endDate: Field.optional(Scalar.checked<string, SemanticTime>("SemanticTime", Scalar.string, _SemanticTime.parse, null)),
      id: Field.optional(Scalar.string),
    });
    /** The wire JSON `raw` as a `SheetCellSchema`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => SheetCellSchema = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetCellSchema`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<SheetCellSchema> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetCellSchema) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace SheetRowDataResponse {
    /** The field table that validates, hydrates and serializes a `SheetRowDataResponse`. */
    export const MODEL: Model.Object<SheetRowDataResponse> = Model.object<SheetRowDataResponse>({
      data: Field.required(Model.array(Model.defer(() => SheetCellSchema.MODEL))),
      dataRange: Field.optional(Model.defer(() => DateRange.MODEL)),
      id: Field.required(Scalar.checked<string, RowId>("RowId", Scalar.string, RowId.of, null)),
      label: Field.optional(Scalar.string),
      lookupKey: Field.optional(Scalar.string),
      metadata: Field.optional(Scalar.json),
      rowType: Field.optional(Scalar.string),
      syncKeys: Field.optional(Model.array(Scalar.string)),
    });
    /** The wire JSON `raw` as a `SheetRowDataResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => SheetRowDataResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SheetRowDataResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<SheetRowDataResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SheetRowDataResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace SyncStatusResponse {
    /** The field table that validates, hydrates and serializes a `SyncStatusResponse`. */
    export const MODEL: Model.Object<SyncStatusResponse> = Model.object<SyncStatusResponse>({
      error: Field.optional(Scalar.string),
      pollUrl: Field.optional(Scalar.string),
      status: Field.required(Scalar.stringUnion(["pending", "running", "complete", "failed"] as const)),
    });
    /** The wire JSON `raw` as a `SyncStatusResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => SyncStatusResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `SyncStatusResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<SyncStatusResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: SyncStatusResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace TracingEntriesRequest {
    /** The field table that validates, hydrates and serializes a `TracingEntriesRequest`. */
    export const MODEL: Model.Object<TracingEntriesRequest> = Model.object<TracingEntriesRequest>({
      entries: Field.required(Model.array(Scalar.json)),
    });
    /** The wire JSON `raw` as a `TracingEntriesRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => TracingEntriesRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `TracingEntriesRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<TracingEntriesRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: TracingEntriesRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace WarmRequest {
    /** The field table that validates, hydrates and serializes a `WarmRequest`. */
    export const MODEL: Model.Object<WarmRequest> = Model.object<WarmRequest>({
      range: Field.optional(Model.defer(() => DateRange.MODEL)),
      rowKeys: Field.required(Model.array(Scalar.string)),
    });
    /** The wire JSON `raw` as a `WarmRequest`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => WarmRequest = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `WarmRequest`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<WarmRequest> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: WarmRequest) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace WarmResponse {
    /** The field table that validates, hydrates and serializes a `WarmResponse`. */
    export const MODEL: Model.Object<WarmResponse> = Model.object<WarmResponse>({
      syncKeys: Field.required(Model.array(Scalar.string)),
    });
    /** The wire JSON `raw` as a `WarmResponse`; throws when it does not match. */
    export const hydrate: (raw: UntypedJson) => WarmResponse = MODEL.hydrate.bind(MODEL);
    /** The wire JSON `raw` as a `WarmResponse`, or the mismatch as an error result. */
    export const safeHydrate: (raw: UntypedJson) => Model.Result<WarmResponse> = MODEL.safeHydrate.bind(MODEL);
    /** `value` as the wire JSON the server reads. */
    export const serialize: (value: WarmResponse) => UntypedJson = MODEL.serialize.bind(MODEL);
  }
  export namespace ApiKeyItem {
    /** The wire JSON `raw` revived as a `ApiKeyItem`. */
    export function parse(raw: UntypedJson): ApiKeyItem {
      raw.createdAt && (raw.createdAt = Utc.parse(raw.createdAt));
      raw.expiresAt && (raw.expiresAt = Utc.parse(raw.expiresAt));
      return raw;
    }
  }
  export namespace ApiKeysListResponse {
    /** The wire JSON `raw` revived as a `ApiKeysListResponse`. */
    export function parse(raw: UntypedJson): ApiKeysListResponse {
      raw.items && (raw.items = raw.items.map(ApiKeyItem.parse));
      return raw;
    }
  }
  export namespace AvailableSlot {
    /** The wire JSON `raw` revived as a `AvailableSlot`. */
    export function parse(raw: UntypedJson): AvailableSlot {
      raw.endTime && (raw.endTime = Utc.parse(raw.endTime));
      raw.startTime && (raw.startTime = Utc.parse(raw.startTime));
      return raw;
    }
  }
  export namespace AvailabilityResponse {
    /** The wire JSON `raw` revived as a `AvailabilityResponse`. */
    export function parse(raw: UntypedJson): AvailabilityResponse {
      raw.availableSlots && (raw.availableSlots = raw.availableSlots.map(AvailableSlot.parse));
      raw.date && (raw.date = NaiveDate.parseOpt(raw.date));
      return raw;
    }
  }
  export namespace Booking {
    /** The wire JSON `raw` revived as a `Booking`. */
    export function parse(raw: UntypedJson): Booking {
      raw.createdAt && (raw.createdAt = Utc.parse(raw.createdAt));
      raw.endTime && (raw.endTime = Utc.parse(raw.endTime));
      raw.id && (raw.id = Uuid.parse(raw.id));
      raw.schedulingLinkId && (raw.schedulingLinkId = Uuid.parse(raw.schedulingLinkId));
      raw.startTime && (raw.startTime = Utc.parse(raw.startTime));
      return raw;
    }
  }
  export namespace CalendarOpenRequest {
    /** The wire JSON `raw` revived as a `CalendarOpenRequest`. */
    export function parse(raw: UntypedJson): CalendarOpenRequest {
      return raw;
    }
  }
  export namespace Changes {
    /** The wire JSON `raw` revived as a `Changes`. */
    export function parse(raw: UntypedJson): Changes {
      return raw;
    }
  }
  export namespace ChangeLogEntry {
    /** The wire JSON `raw` revived as a `ChangeLogEntry`. */
    export function parse(raw: UntypedJson): ChangeLogEntry {
      raw.changes && (raw.changes = Changes.parse(raw.changes));
      raw.date && (raw.date = NaiveDate.parseOpt(raw.date));
      return raw;
    }
  }
  export namespace ModelInfo {
    /** The wire JSON `raw` revived as a `ModelInfo`. */
    export function parse(raw: UntypedJson): ModelInfo {
      return raw;
    }
  }
  export namespace ProviderInfo {
    /** The wire JSON `raw` revived as a `ProviderInfo`. */
    export function parse(raw: UntypedJson): ProviderInfo {
      raw.models && (raw.models = raw.models.map(ModelInfo.parse));
      return raw;
    }
  }
  export namespace ChatModelsResponse {
    /** The wire JSON `raw` revived as a `ChatModelsResponse`. */
    export function parse(raw: UntypedJson): ChatModelsResponse {
      raw.providers && (raw.providers = raw.providers.map(ProviderInfo.parse));
      return raw;
    }
  }
  export namespace ChatRequest {
    /** The wire JSON `raw` revived as a `ChatRequest`. */
    export function parse(raw: UntypedJson): ChatRequest {
      return raw;
    }
  }
  export namespace ClaimResponse {
    /** The wire JSON `raw` revived as a `ClaimResponse`. */
    export function parse(raw: UntypedJson): ClaimResponse {
      return raw;
    }
  }
  export namespace CrdtSyncPayload {
    /** The wire JSON `raw` revived as a `CrdtSyncPayload`. */
    export function parse(raw: UntypedJson): CrdtSyncPayload {
      return raw;
    }
  }
  export namespace CrdtEnvelope {
    /** The wire JSON `raw` revived as a `CrdtEnvelope`. */
    export function parse(raw: UntypedJson): CrdtEnvelope {
      raw.payload && (raw.payload = CrdtSyncPayload.parse(raw.payload));
      return raw;
    }
  }
  export namespace CrdtBootstrapResponse {
    /** The wire JSON `raw` revived as a `CrdtBootstrapResponse`. */
    export function parse(raw: UntypedJson): CrdtBootstrapResponse {
      raw.snapshotEnvelope && (raw.snapshotEnvelope = CrdtEnvelope.parse(raw.snapshotEnvelope));
      return raw;
    }
  }
  export namespace CrdtCreateRoomRequest {
    /** The wire JSON `raw` revived as a `CrdtCreateRoomRequest`. */
    export function parse(raw: UntypedJson): CrdtCreateRoomRequest {
      return raw;
    }
  }
  export namespace CrdtCreateRoomResponse {
    /** The wire JSON `raw` revived as a `CrdtCreateRoomResponse`. */
    export function parse(raw: UntypedJson): CrdtCreateRoomResponse {
      raw.createdAt && (raw.createdAt = Utc.parse(raw.createdAt));
      raw.expiresAt && (raw.expiresAt = Utc.parse(raw.expiresAt));
      return raw;
    }
  }
  export namespace CrdtEnvelopeRecord {
    /** The wire JSON `raw` revived as a `CrdtEnvelopeRecord`. */
    export function parse(raw: UntypedJson): CrdtEnvelopeRecord {
      raw.envelope && (raw.envelope = CrdtEnvelope.parse(raw.envelope));
      raw.receivedAt && (raw.receivedAt = Utc.parse(raw.receivedAt));
      return raw;
    }
  }
  export namespace CrdtRoomSummary {
    /** The wire JSON `raw` revived as a `CrdtRoomSummary`. */
    export function parse(raw: UntypedJson): CrdtRoomSummary {
      raw.createdAt && (raw.createdAt = Utc.parse(raw.createdAt));
      raw.expiresAt && (raw.expiresAt = Utc.parse(raw.expiresAt));
      return raw;
    }
  }
  export namespace CrdtListRoomsResponse {
    /** The wire JSON `raw` revived as a `CrdtListRoomsResponse`. */
    export function parse(raw: UntypedJson): CrdtListRoomsResponse {
      raw.rooms && (raw.rooms = raw.rooms.map(CrdtRoomSummary.parse));
      return raw;
    }
  }
  export namespace CrdtPostEventRequest {
    /** The wire JSON `raw` revived as a `CrdtPostEventRequest`. */
    export function parse(raw: UntypedJson): CrdtPostEventRequest {
      raw.envelope && (raw.envelope = CrdtEnvelope.parse(raw.envelope));
      return raw;
    }
  }
  export namespace CrdtPullEventsResponse {
    /** The wire JSON `raw` revived as a `CrdtPullEventsResponse`. */
    export function parse(raw: UntypedJson): CrdtPullEventsResponse {
      raw.events && (raw.events = raw.events.map(CrdtEnvelopeRecord.parse));
      return raw;
    }
  }
  export namespace CreateBookingRequest {
    /** The wire JSON `raw` revived as a `CreateBookingRequest`. */
    export function parse(raw: UntypedJson): CreateBookingRequest {
      raw.startTime && (raw.startTime = Utc.parse(raw.startTime));
      return raw;
    }
  }
  export namespace CreatedApiKeyResponse {
    /** The wire JSON `raw` revived as a `CreatedApiKeyResponse`. */
    export function parse(raw: UntypedJson): CreatedApiKeyResponse {
      raw.expiresAt && (raw.expiresAt = Utc.parse(raw.expiresAt));
      return raw;
    }
  }
  export namespace DateRange {
    /** The wire JSON `raw` revived as a `DateRange`. */
    export function parse(raw: UntypedJson): DateRange {
      raw.end && (raw.end = NaiveDate.parseOpt(raw.end));
      raw.start && (raw.start = NaiveDate.parseOpt(raw.start));
      return raw;
    }
  }
  export namespace SyncStateInfo {
    /** The wire JSON `raw` revived as a `SyncStateInfo`. */
    export function parse(raw: UntypedJson): SyncStateInfo {
      raw.futureLastSync && (raw.futureLastSync = Utc.parse(raw.futureLastSync));
      raw.futureSyncedTo && (raw.futureSyncedTo = NaiveDate.parseOpt(raw.futureSyncedTo));
      raw.historicalSyncedTo && (raw.historicalSyncedTo = NaiveDate.parseOpt(raw.historicalSyncedTo));
      raw.lastSyncTime && (raw.lastSyncTime = Utc.parse(raw.lastSyncTime));
      return raw;
    }
  }
  export namespace WeatherRowInfo {
    /** The wire JSON `raw` revived as a `WeatherRowInfo`. */
    export function parse(raw: UntypedJson): WeatherRowInfo {
      raw.syncState && (raw.syncState = SyncStateInfo.parse(raw.syncState));
      return raw;
    }
  }
  export namespace DebugWeatherResponse {
    /** The wire JSON `raw` revived as a `DebugWeatherResponse`. */
    export function parse(raw: UntypedJson): DebugWeatherResponse {
      raw.rows && (raw.rows = raw.rows.map(WeatherRowInfo.parse));
      return raw;
    }
  }
  export namespace Exposure {
    /** The wire JSON `raw` revived as a `Exposure`. */
    export function parse(raw: UntypedJson): Exposure {
      return raw;
    }
  }
  export namespace ObservationReceipt {
    /** The wire JSON `raw` revived as a `ObservationReceipt`. */
    export function parse(raw: UntypedJson): ObservationReceipt {
      return raw;
    }
  }
  export namespace ObservationRequest {
    /** The wire JSON `raw` revived as a `ObservationRequest`. */
    export function parse(raw: UntypedJson): ObservationRequest {
      return raw;
    }
  }
  export namespace OnboardingStepRequest {
    /** The wire JSON `raw` revived as a `OnboardingStepRequest`. */
    export function parse(raw: UntypedJson): OnboardingStepRequest {
      return raw;
    }
  }
  export namespace PageMeta {
    /** The wire JSON `raw` revived as a `PageMeta`. */
    export function parse(raw: UntypedJson): PageMeta {
      return raw;
    }
  }
  export namespace PageNode {
    /** The wire JSON `raw` revived as a `PageNode`. */
    export function parse(raw: UntypedJson): PageNode {
      raw.children && (raw.children = raw.children.map(PageNode.parse));
      return raw;
    }
  }
  export namespace PageModel {
    /** The wire JSON `raw` revived as a `PageModel`. */
    export function parse(raw: UntypedJson): PageModel {
      raw.meta && (raw.meta = PageMeta.parse(raw.meta));
      raw.nodes && (raw.nodes = raw.nodes.map(PageNode.parse));
      return raw;
    }
  }
  export namespace PromoCampaignCode {
    /** The wire JSON `raw` revived as a `PromoCampaignCode`. */
    export function parse(raw: UntypedJson): PromoCampaignCode {
      return raw;
    }
  }
  export namespace PromoCampaignRequest {
    /** The wire JSON `raw` revived as a `PromoCampaignRequest`. */
    export function parse(raw: UntypedJson): PromoCampaignRequest {
      return raw;
    }
  }
  export namespace PromoCampaignResponse {
    /** The wire JSON `raw` revived as a `PromoCampaignResponse`. */
    export function parse(raw: UntypedJson): PromoCampaignResponse {
      raw.codes && (raw.codes = raw.codes.map(PromoCampaignCode.parse));
      return raw;
    }
  }
  export namespace SheetCellSchema {
    /** The wire JSON `raw` revived as a `SheetCellSchema`. */
    export function parse(raw: UntypedJson): SheetCellSchema {
      raw.date && (raw.date = _SemanticTime.parse(raw.date));
      raw.endDate && (raw.endDate = _SemanticTime.parse(raw.endDate));
      return raw;
    }
  }
  export namespace SheetRowDataResponse {
    /** The wire JSON `raw` revived as a `SheetRowDataResponse`. */
    export function parse(raw: UntypedJson): SheetRowDataResponse {
      raw.data && (raw.data = raw.data.map(SheetCellSchema.parse));
      raw.dataRange && (raw.dataRange = DateRange.parse(raw.dataRange));
      raw.id && (raw.id = RowId.of(raw.id));
      return raw;
    }
  }
  export namespace SyncStatusResponse {
    /** The wire JSON `raw` revived as a `SyncStatusResponse`. */
    export function parse(raw: UntypedJson): SyncStatusResponse {
      return raw;
    }
  }
  export namespace TracingEntriesRequest {
    /** The wire JSON `raw` revived as a `TracingEntriesRequest`. */
    export function parse(raw: UntypedJson): TracingEntriesRequest {
      return raw;
    }
  }
  export namespace WarmRequest {
    /** The wire JSON `raw` revived as a `WarmRequest`. */
    export function parse(raw: UntypedJson): WarmRequest {
      raw.range && (raw.range = DateRange.parse(raw.range));
      return raw;
    }
  }
  export namespace WarmResponse {
    /** The wire JSON `raw` revived as a `WarmResponse`. */
    export function parse(raw: UntypedJson): WarmResponse {
      return raw;
    }
  }
  export type Routes = {
    "/api/build": {
      get: {
      }
    }
    "/api/build/changelog": {
      get: {
        response: ChangeLogEntry
      }
    }
    "/api/build/changelog/list": {
      get: {
        response: ChangeLogEntry[]
      }
    }
    "/api/debug/cells": {
      get: {
        config: {
          query: {
            row: string,
            start: NaiveDate,
            end: NaiveDate,
          }
        }
        response: SheetRowDataResponse
      }
    }
    "/api/debug/crdt/rooms": {
      get: {
        response: CrdtListRoomsResponse
      }
      post: {
        config: {
          body: CrdtCreateRoomRequest
        }
        response: CrdtCreateRoomResponse
      }
    }
    "/api/debug/crdt/rooms/{roomId}/bootstrap": {
      get: {
        config: {
          query: {
            roomToken: Option<string>,
          }
          path: {
            roomId: string
          }
        }
        response: CrdtBootstrapResponse
      }
    }
    "/api/debug/crdt/rooms/{roomId}/events": {
      get: {
        config: {
          query: {
            roomToken: Option<string>,
            cursor: Option<number>,
            limit: Option<number>,
          }
          path: {
            roomId: string
          }
        }
        response: CrdtPullEventsResponse
      }
      post: {
        config: {
          path: {
            roomId: string
          }
          body: CrdtPostEventRequest
        }
        response: CrdtEnvelopeRecord
      }
    }
    "/api/debug/weather": {
      get: {
        response: DebugWeatherResponse
      }
    }
    "/api/debug/weather/{lid}/clear": {
      post: {
        config: {
          path: {
            lid: number
          }
        }
      }
    }
    "/api/events/observations": {
      post: {
        config: {
          body: ObservationRequest
        }
      }
    }
    "/api/funnel/calendar-open": {
      post: {
        config: {
          body: CalendarOpenRequest
        }
      }
    }
    "/api/funnel/onboarding-step": {
      post: {
        config: {
          body: OnboardingStepRequest
        }
      }
    }
    "/api/funnel/signin-interface": {
      post: {
        config: {
          body: Exposure
        }
      }
    }
    "/api/mock/api-keys": {
      get: {
        response: ApiKeysListResponse
      }
    }
    "/api/mock/chat/models": {
      get: {
        response: ChatModelsResponse
      }
    }
    "/api/mock/chat/stream": {
      post: {
        config: {
          body: ChatRequest
        }
      }
    }
    "/api/public/brand/pages": {
      get: {
        config: {
          query: {
            route: string,
            query: Option<string>,
            category: Option<string>,
            limit: Option<number>,
            offset: Option<number>,
          }
        }
        response: PageModel
      }
    }
    "/api/public/promo/campaign": {
      post: {
        config: {
          body: PromoCampaignRequest
        }
        response: PromoCampaignResponse
      }
    }
    "/api/public/promo/claim": {
      post: {
        config: {
          query: {
            code: string,
          }
        }
        response: ClaimResponse
      }
    }
    "/api/public/scheduling-links/{slug}/availability/{date}": {
      get: {
        config: {
          query: {
            username: Option<string>,
          }
          path: {
            slug: string
            date: string
          }
        }
        response: AvailabilityResponse
      }
    }
    "/api/public/scheduling-links/{slug}/bookings": {
      post: {
        config: {
          query: {
            username: Option<string>,
          }
          path: {
            slug: string
          }
          body: CreateBookingRequest
        }
        response: Booking
      }
    }
    "/api/sync/warm": {
      post: {
        config: {
          body: WarmRequest
        }
        response: WarmResponse
      }
    }
    "/api/sync/{syncKey}/status": {
      get: {
        config: {
          path: {
            syncKey: string
          }
        }
        response?: Option<SyncStatusResponse>
      }
    }
    "/api/tracing/entries": {
      post: {
        config: {
          body: TracingEntriesRequest
        }
      }
    }
    "/api/user/api-keys": {
      get: {
        response: ApiKeysListResponse
      }
      post: {
        response: CreatedApiKeyResponse
      }
    }
  };
  export const REVIVERS = {
    "/api/build/changelog": {
      parse: {
        get: ChangeLogEntry.parse,
      },
      validate: {
        get: ChangeLogEntry.safeHydrate,
      },
    },
    "/api/build/changelog/list": {
      parse: {
        get: (p: UntypedJson[]) => p.map(ChangeLogEntry.parse),
      },
      validate: {
        get: (p: UntypedJson) => Model.array(ChangeLogEntry.MODEL).safeHydrate(p),
      },
    },
    "/api/debug/cells": {
      parse: {
        get: SheetRowDataResponse.parse,
      },
      validate: {
        get: SheetRowDataResponse.safeHydrate,
      },
    },
    "/api/debug/crdt/rooms": {
      parse: {
        get: CrdtListRoomsResponse.parse,
        post: CrdtCreateRoomResponse.parse,
      },
      validate: {
        get: CrdtListRoomsResponse.safeHydrate,
        post: CrdtCreateRoomResponse.safeHydrate,
      },
    },
    "/api/debug/crdt/rooms/{roomId}/bootstrap": {
      parse: {
        get: CrdtBootstrapResponse.parse,
      },
      validate: {
        get: CrdtBootstrapResponse.safeHydrate,
      },
    },
    "/api/debug/crdt/rooms/{roomId}/events": {
      parse: {
        get: CrdtPullEventsResponse.parse,
        post: CrdtEnvelopeRecord.parse,
      },
      validate: {
        get: CrdtPullEventsResponse.safeHydrate,
        post: CrdtEnvelopeRecord.safeHydrate,
      },
    },
    "/api/debug/weather": {
      parse: {
        get: DebugWeatherResponse.parse,
      },
      validate: {
        get: DebugWeatherResponse.safeHydrate,
      },
    },
    "/api/mock/api-keys": {
      parse: {
        get: ApiKeysListResponse.parse,
      },
      validate: {
        get: ApiKeysListResponse.safeHydrate,
      },
    },
    "/api/mock/chat/models": {
      parse: {
        get: ChatModelsResponse.parse,
      },
      validate: {
        get: ChatModelsResponse.safeHydrate,
      },
    },
    "/api/public/brand/pages": {
      parse: {
        get: PageModel.parse,
      },
      validate: {
        get: PageModel.safeHydrate,
      },
    },
    "/api/public/promo/campaign": {
      parse: {
        post: PromoCampaignResponse.parse,
      },
      validate: {
        post: PromoCampaignResponse.safeHydrate,
      },
    },
    "/api/public/promo/claim": {
      parse: {
        post: ClaimResponse.parse,
      },
      validate: {
        post: ClaimResponse.safeHydrate,
      },
    },
    "/api/public/scheduling-links/{slug}/availability/{date}": {
      parse: {
        get: AvailabilityResponse.parse,
      },
      validate: {
        get: AvailabilityResponse.safeHydrate,
      },
    },
    "/api/public/scheduling-links/{slug}/bookings": {
      parse: {
        post: Booking.parse,
      },
      validate: {
        post: Booking.safeHydrate,
      },
    },
    "/api/sync/warm": {
      parse: {
        post: WarmResponse.parse,
      },
      validate: {
        post: WarmResponse.safeHydrate,
      },
    },
    "/api/sync/{syncKey}/status": {
      parse: {
        get: SyncStatusResponse.parse,
      },
      validate: {
        get: SyncStatusResponse.safeHydrate,
      },
    },
    "/api/user/api-keys": {
      parse: {
        get: ApiKeysListResponse.parse,
        post: CreatedApiKeyResponse.parse,
      },
      validate: {
        get: ApiKeysListResponse.safeHydrate,
        post: CreatedApiKeyResponse.safeHydrate,
      },
    },
  };
}
